import type { ShipmentRouteBatchRow } from '@gpx-spa/queries/src/getAccountShipmentRoutesBatch';
import type { ShipmentsTrackBatchRow } from '@gpx-spa/queries/src/getAccountShipmentsTracksBatch';

/** Fast non-cryptographic fingerprint for cache-skip decisions (map layer repaints). */
function simpleStringFingerprint(payload: string): string {
  let h = 5381 >>> 0;
  for (let i = 0; i < payload.length; i++) {
    h = (Math.imul(33, h) ^ payload.charCodeAt(i)!) >>> 0;
  }
  return `${h.toString(16)}:${payload.length}`;
}

/**
 * Stable fingerprint for path (shipment track) + route line layers.
 * Any change to encoded polylines, track deltas, or line color source should change the hash.
 */
export function fingerprintPathAndRouteLineLayers(
  shipmentId: number,
  trackRow: ShipmentsTrackBatchRow | null | undefined,
  routes: ShipmentRouteBatchRow[],
  shipmentStatus: string | undefined,
  routeGeometryQualityPercent = 100,
): string {
  const trackPayload = trackRow
    ? {
        shipment_id: trackRow.shipment_id,
        device_id: trackRow.device_id,
        encoded_polyline: trackRow.data?.encoded_polyline ?? '',
        timestamp: trackRow.data?.timestamp ?? '',
        battery: trackRow.data?.battery ?? '',
        fix_type: trackRow.data?.fix_type ?? '',
        accuracy: trackRow.data?.accuracy ?? '',
        count: trackRow.data?.count,
      }
    : null;

  const forRoutes = routes
    .filter((s) => s.shipment_id === shipmentId)
    .sort((a, b) => a.id - b.id)
    .map((r) => ({
      id: r.id,
      opts: (r.route_options ?? []).map((o) => o.encoded_polyline ?? ''),
    }));

  const raw = JSON.stringify({
    v: 2,
    shipmentId,
    shipmentStatus: shipmentStatus ?? '',
    routeGeometryQualityPercent,
    track: trackPayload,
    routes: forRoutes,
  });
  return simpleStringFingerprint(raw);
}
